import DISPLAY_SIZE_OPTIONS, { SIZE_OPTIONS } from '~shared/constants/sizes';

// CSS Constants
/**
 * Breakpoints to delineate special handling
 */
// If mixing lowerBPMediaQUery with upperBPMediaQuery at same WrenchTheme.breakpoints[size],
// most recent (in sequence) or most specific css rules would apply.
// For MDS_MOBILE (768 and below rule) in conjunction with MDS_TABLET(768 and above rule), place
// MDS_MOBILE first, followed by MDS_TABLET
const lowerBPMediaQuery = (size: string | number, overrideSize?: number): string => {
  return `@media only screen and (min-width: ${overrideSize ?? DISPLAY_SIZE_OPTIONS[size]}px)`;
};

/**
 * Use this if you need an in-between media query for styles that only affect the component when the screen is between two screen sizes.
 * This allows you to not have to specify already defined styles at a lower breakpoint that need to be reapplied to a higher breakpoint
 *
 * Example:
 * someClass: {
 *    fontSize: 16px,
 *
 *    [BETWEEN_MD_LG_MEDIA_QUERY]: {
 *      fontSize: 12px,
 *    }
 * }
 *
 * In the example above, the fontSize: 12px would be applied on screen sizes between 768px and 992px, all other screen sizes would default 16px.
 * Without the between query, the following would need to be used to achieve the same:
 *
 *  someClass: {
 *    fontSize: 16px,
 *
 *    [MD_MEDIA_QUERY]: {
 *      fontSize: 12px,
 *    },
 *
 *    [LG_MEDIA_QUERY]: {
 *      fontSize: 16px,
 *    },
 * }
 */

const lowerBPMBetweenMediaQuery = (minSize: string | number, maxSize: string | number): string => {
  return `@media only screen and (min-width: ${DISPLAY_SIZE_OPTIONS[minSize]}px) and (max-width: ${DISPLAY_SIZE_OPTIONS[maxSize]}px)`;
};

/**
 * This set of media queries are designed to work with Wrench's breakpoints.
 * In particular, use alongside GridPlus/Wrench Grid and other <div>s (e.g., FlexContainer).
 *
 * NOTE: media queries cascade from small to large.
 * Thus specify in the following order for ease of use, ease of understanding, and to leverage CSS
 * cascading and rule specificity precedence.
 * XS_MEDIA_QUERY, SM_MEDIA_QUERY, MD_MEDIA_QUERY, LG_MEDIA_QUERY, XL_MEDIA_QUERY.

* All browser widths > WrenchTheme.breakpoints.xl conform to XL_MEDIA_QUERY.
 * Similarly, if XS_MEDIA_QUERY is not stated and others are stated, e.g., SM_MEDIA_QUERY,
 * then widths from 0 to < WrenchTheme.breakpoints.sm use default CSS rules.
 */
// Wrench.breakpoints - lg: '992px'
export const LG_MEDIA_QUERY = lowerBPMediaQuery(SIZE_OPTIONS.lg);
// Wrench.breakpoints - md: '768px'
export const MD_MEDIA_QUERY = lowerBPMediaQuery(SIZE_OPTIONS.md);
// Wrench.breakpoints - sm: '576px'
export const SM_MEDIA_QUERY = lowerBPMediaQuery(SIZE_OPTIONS.sm);
// MDS_SMALL_BREAKPOINT = '320px'
export const SM_MOBILE_MEDIA_QUERY = lowerBPMediaQuery(SIZE_OPTIONS['mds-sm']);
// Wrench.breakpoints - xs: '0'
export const XS_MEDIA_QUERY = lowerBPMediaQuery(SIZE_OPTIONS.xs);
// Wrench.breakpoints - xl: '1200px'
export const XL_MEDIA_QUERY = lowerBPMediaQuery(SIZE_OPTIONS.xl);

// Wrench.breakpoints - between md: '768px' and lg: '992px'
export const BETWEEN_MD_LG_MEDIA_QUERY = lowerBPMBetweenMediaQuery(SIZE_OPTIONS.md, SIZE_OPTIONS.lg);
// Wrench.breakpoints - between lg: '992px and xl: 1200px'
export const BETWEEN_LG_XL_MEDIA_QUERY = lowerBPMBetweenMediaQuery(SIZE_OPTIONS.lg, SIZE_OPTIONS.xl);
